import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth (SaaS foundation)
export const login = (email, password) => api.post("/auth/login", { email, password });
export const register = (data) => api.post("/auth/register", data);
export const getMe = () => api.get("/auth/me");
export const changePassword = (currentPassword, newPassword) =>
  api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
export const seedData = () => api.post("/seed");

// Profile
export const getProfile = () => api.get("/profile");
export const updateProfile = (data) => api.put("/profile", data);
export const checkProfileComplete = () => api.get("/profile/check");

// Billing (SaaS) — GET /api/billing/status en FastAPI (fuente única; no depender de Vercel serverless)
export const getBillingStatus = () => api.get("/billing/status");
export const requestExport = () => api.post("/export/request");
export const getExportStatus = (exportId) => api.get(`/export/status/${exportId}`);

// Patients
export const getPatients = (status) =>
  status ? api.get("/patients", { params: { status } }) : api.get("/patients");
export const getPatient = (id) => api.get(`/patients/${id}`);
export const createPatient = (data) => api.post("/patients", data);
export const updatePatient = (id, data) => api.put(`/patients/${id}`, data);
export const deletePatient = (id) => api.delete(`/patients/${id}`);
export const archivePatient = (id) => api.patch(`/patients/${id}/archive`);
export const restorePatient = (id) => api.patch(`/patients/${id}/restore`);

// Consultations
export const getConsultations = () => api.get("/consultations");
export const getPatientConsultations = (patientId) => api.get(`/consultations/patient/${patientId}`);
export const getFollowUpContext = (patientId) => api.get(`/consultations/follow-up-context/${patientId}`);
export const getConsultation = (id) => api.get(`/consultations/${id}`);
export const createConsultation = (data) => api.post("/consultations", data);
export const updateConsultation = (id, data) => api.put(`/consultations/${id}`, data);

// Scales
export const getScales = () => api.get("/scales");
export const getPatientScales = (patientId) => api.get(`/scales/patient/${patientId}`);
export const getPatientLatestScales = (patientId) => api.get(`/scales/patient/${patientId}/latest`);
export const getPatientScaleHistory = (patientId, scaleType) => api.get(`/scales/patient/${patientId}/history/${scaleType}`);
export const createScale = (data) => api.post("/scales", data);

// Appointments (administrative scheduling contract — no DELETE)
function cleanAppointmentParams(filters = {}) {
  const params = {};
  Object.entries(filters || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params[key] = value;
  });
  return params;
}

export const getAppointments = (filters = {}) =>
  api.get("/appointments", { params: cleanAppointmentParams(filters) });
export const getAppointment = (appointmentId) => api.get(`/appointments/${appointmentId}`);
export const createAppointment = (data) => api.post("/appointments", data);
export const updateAppointment = (appointmentId, data) =>
  api.put(`/appointments/${appointmentId}`, data);
export const updateAppointmentStatus = (appointmentId, data) =>
  api.patch(`/appointments/${appointmentId}/status`, data);

export const getExternalAssessments = (patientId) =>
  api.get("/external-assessments", { params: { patient_id: patientId } });
export const getExternalAssessment = (id) => api.get(`/external-assessments/${id}`);
export const createExternalAssessment = (payload) => api.post("/external-assessments", payload);
export const updateExternalAssessment = (id, payload) =>
  api.put(`/external-assessments/${id}`, payload);
export const voidExternalAssessment = (id, reason) =>
  api.patch(`/external-assessments/${id}/void`, { void_reason: reason });

// Dashboard
export const getDashboardStats = () => api.get("/dashboard/stats");
export const getAdvancedAlerts = () => api.get("/dashboard/advanced-alerts");
export const getFollowUpSuggestions = () => api.get("/dashboard/follow-up-suggestions");
export const getClinicalSummary = () => api.get("/dashboard/clinical-summary");
export const getUsDemoDashboard = () => api.get("/dashboard/us-demo");
export const postUsSteadiPreview = (payload) => api.post("/dashboard/us/steadi-preview", payload);
export const postUsMoodPreview = (payload) => api.post("/dashboard/us/mood-preview", payload);
export const postUsCognitionPreview = (payload) => api.post("/dashboard/us/cognition-preview", payload);
export const postUsFunctionalPreview = (payload) => api.post("/dashboard/us/functional-preview", payload);
export const postUsFrailtyPreview = (payload) => api.post("/dashboard/us/frailty-preview", payload);

// BEERS Review
export const getPatientBEERSStatus = (patientId) => api.get(`/beers/patient/${patientId}`);

// CDSS (Clinical Decision Support)
export const getChronicConditionsList = () => api.get("/cdss/chronic-conditions");
export const generateCDSSRecommendations = (patientId, vitals) => api.post(`/cdss/recommendations/${patientId}`, vitals);
export const calculateBMI = (data) => api.post("/cdss/calculate-bmi", data);

// Consultation Drafts (Save Progress)
export const getDrafts = () => api.get("/consultation-drafts");
export const getPatientDraft = (patientId) => api.get(`/consultation-drafts/patient/${patientId}`);
export const saveDraft = (data) => api.post("/consultation-drafts", data);
export const updateDraft = (draftId, data) => api.put(`/consultation-drafts/${draftId}`, data);
export const deleteDraft = (draftId) => api.delete(`/consultation-drafts/${draftId}`);
export const deletePatientDraft = (patientId) => api.delete(`/consultation-drafts/patient/${patientId}`);

// PDF
export const getPrescriptionPDF = (consultationId) => 
  api.get(`/pdf/prescription/${consultationId}`, { responseType: "blob" });
export const getReportPDF = (consultationId) => 
  api.get(`/pdf/action-plan/${consultationId}`, { responseType: "blob" });
export const getEvolutionPDF = (patientId) => 
  api.get(`/pdf/evolution/${patientId}`, { responseType: "blob" });
export const getFamilyGuidePDF = (consultationId) =>
  api.get(`/pdf/geriatric-plan/${consultationId}`, { responseType: "blob" });
export const getFamilySimplePDF = (consultationId) =>
  api.get(`/pdf/family-simple/${consultationId}`, { responseType: "blob" });
export const getUsAwvClinicalSummaryPDF = (consultationId) =>
  api.get(`/pdf/us/awv-clinical-summary/${consultationId}`, { responseType: "blob" });
export const getUsPersonalizedPreventionPlanPDF = (consultationId) =>
  api.get(`/pdf/us/personalized-prevention-plan/${consultationId}`, { responseType: "blob" });
export const getUsPatientCaregiverSummaryPDF = (consultationId) =>
  api.get(`/pdf/us/patient-caregiver-summary/${consultationId}`, { responseType: "blob" });
export const getUsDemoAwvClinicalSummaryPDF = (demoVisitId) =>
  api.get(`/pdf/us/demo/awv-clinical-summary/${demoVisitId}`, { responseType: "blob" });
export const getUsDemoPersonalizedPreventionPlanPDF = (demoVisitId) =>
  api.get(`/pdf/us/demo/personalized-prevention-plan/${demoVisitId}`, { responseType: "blob" });
export const getUsDemoPatientCaregiverSummaryPDF = (demoVisitId) =>
  api.get(`/pdf/us/demo/patient-caregiver-summary/${demoVisitId}`, { responseType: "blob" });

// AI Writing Assistant
export const generateAISummary = (data) => api.post("/ai/consultation-summary", data);

// Admin (requires is_admin)
export const getAdminDashboard = () => api.get("/admin/dashboard");
export const getAdminContacts = (params) => api.get("/admin/contacts", { params });
export const createAdminContact = (data) => api.post("/admin/contacts", data);
export const getAdminContact = (id) => api.get(`/admin/contacts/${id}`);
export const updateAdminContact = (id, data) => api.put(`/admin/contacts/${id}`, data);
export const updateAdminContactStatus = (id, status) => api.patch(`/admin/contacts/${id}/status`, { status });

export const getAdminUsers = () => api.get("/admin/users");
export const updateAdminUser = (userId, data) => api.put(`/admin/users/${userId}`, data);
export const deleteAdminUser = (userId) => api.delete(`/admin/users/${userId}`);

export const getAdminFeedback = (params) => api.get("/admin/feedback", { params });
export const createAdminFeedback = (data) => api.post("/admin/feedback", data);
export const getAdminFeedbackItem = (id) => api.get(`/admin/feedback/${id}`);
export const updateAdminFeedback = (id, data) => api.put(`/admin/feedback/${id}`, data);
export const updateAdminFeedbackStatus = (id, status) => api.patch(`/admin/feedback/${id}/status`, { status });

export const getAdminAdvertising = (params) => api.get("/admin/advertising", { params });
export const createAdminAdvertising = (data) => api.post("/admin/advertising", data);
export const getAdminAdvertisingItem = (id) => api.get(`/admin/advertising/${id}`);
export const updateAdminAdvertising = (id, data) => api.put(`/admin/advertising/${id}`, data);
export const updateAdminAdvertisingStatus = (id, status) => api.patch(`/admin/advertising/${id}/status`, { status });

export const getAdminAnalytics = () => api.get("/admin/analytics");
export const getAdminUsageMetrics = (trendDays = 7) =>
  api.get("/admin/usage-metrics", { params: { trend_days: trendDays } });

/** Flujo oficial entregable → impresión; sin datos de paciente (solo tipo de documento). */
export const trackInformeEntregable = (reportType) =>
  api.post("/usage/informe-entregable", { report_type: reportType });
export const getAdminSettings = () => api.get("/admin/settings");
export const updateAdminSettings = (data) => api.put("/admin/settings", data);

// Aurora Aula admin (requires is_admin)
export const getAulaAdminOrganizations = () => api.get("/aula-admin/organizations");
export const createAulaAdminOrganization = (data) => api.post("/aula-admin/organizations", data);
export const updateAulaAdminOrganization = (id, data) => api.put(`/aula-admin/organizations/${id}`, data);
export const deleteAulaAdminOrganization = (id) => api.delete(`/aula-admin/organizations/${id}`);
export const getAulaAdminCohorts = () => api.get("/aula-admin/cohorts");
export const getAulaAdminCohort = (id) => api.get(`/aula-admin/cohorts/${id}`);
export const createAulaAdminCohort = (data) => api.post("/aula-admin/cohorts", data);

/** Formularios del sitio público: sin instancia `api` (evita Bearer y redirect 401). */
const PUBLIC_API_ROOT = `${(process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8000").replace(/\/$/, "")}/api`;

export const submitPublicContactForm = (data) =>
  axios.post(`${PUBLIC_API_ROOT}/admin/public/contact`, data);

export const submitPublicAdvertisingForm = (data) =>
  axios.post(`${PUBLIC_API_ROOT}/admin/public/advertising`, data);

export default api;
